import type { ObMetricCardData } from './dashboard';

export type ObReportPeriod = '24h' | '7d' | '30d' | '90d' | '365d' | 'all';

export const OB_REPORT_PERIOD_OPTIONS: { value: ObReportPeriod; label: string }[] = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '365d', label: '365d' },
  { value: 'all', label: 'All' },
];

export const OB_REPORT_STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: '1st_call_attempted', label: '1st call attempted' },
  { value: '2nd_call_attempted', label: '2nd call attempted' },
  { value: '3rd_call_attempted_not_verified', label: '3rd call attempted-not verified' },
  { value: 'verified_synced', label: 'Verified & Synced' },
  { value: 'payment_processed', label: 'Payment Processed' },
  { value: 'payment_failed', label: 'Payment Failed' },
] as const;

export function buildReportKpiMetrics(input: {
  totalAppointments: number;
  moneyCollected: number;
  totalAIVerified: number;
  previous?: {
    totalAppointments: number;
    moneyCollected: number;
    totalAIVerified: number;
  };
}): ObMetricCardData[] {
  const calc = (current: number, previous: number) => {
    if (previous === 0) return { value: '100%', type: 'increase' as const };
    const change = ((current - previous) / previous) * 100;
    return {
      value: `${change > 0 ? '+' : ''}${change.toFixed(1)}%`,
      type: change > 0 ? ('increase' as const) : change < 0 ? ('decrease' as const) : ('neutral' as const),
    };
  };

  return [
    {
      title: 'Total Appointments',
      value: input.totalAppointments.toLocaleString(),
      ...(input.previous
        ? { change: calc(input.totalAppointments, input.previous.totalAppointments) }
        : {}),
      icon: 'calendar' as const,
    },
    {
      title: 'Money Collected',
      value: `$${input.moneyCollected.toLocaleString()}`,
      ...(input.previous
        ? { change: calc(input.moneyCollected, input.previous.moneyCollected) }
        : {}),
      icon: 'dollar' as const,
    },
    {
      title: 'AI Verified',
      value: input.totalAIVerified.toLocaleString(),
      ...(input.previous
        ? { change: calc(input.totalAIVerified, input.previous.totalAIVerified) }
        : {}),
      icon: 'sparkle' as const,
    },
  ];
}

export function periodToDateRange(period: ObReportPeriod): { start: Date; end: Date } {
  const end = new Date();
  const start = new Date();
  switch (period) {
    case '24h':
      start.setTime(end.getTime() - 24 * 60 * 60 * 1000);
      break;
    case '7d':
      start.setTime(end.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case '30d':
      start.setTime(end.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case '90d':
      start.setTime(end.getTime() - 90 * 24 * 60 * 60 * 1000);
      break;
    case '365d':
      start.setTime(end.getTime() - 365 * 24 * 60 * 60 * 1000);
      break;
    case 'all':
      start.setTime(0);
      break;
    default:
      start.setTime(end.getTime() - 30 * 24 * 60 * 60 * 1000);
  }
  return { start, end };
}

export function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
